/* import __COLOCATED_TEMPLATE__ from './opamps.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import OpAmpsService from 'filter-wizard/services/opamps';
import SallenKeyLowPassIdealOpAmp from 'filter-wizard/models/implementation/sallen-key/low-pass/ideal-op-amp';
import type {
  DeviceColumn,
  PackedFilterValue,
  SortColumnRef,
} from '@ebiz/ember-aditools-device-table/components/aditools-device-table';
import {
  convertColumnDataForSelectionTable,
  convertDeviceDataForSelectionTable,
  reduceColumnFilterValuesToThosePresent,
} from '@ebiz/ember-aditools-device-table/utils/device-table-pst';
import type {
  DatabookStore,
  DefaultColumnData,
  DatabookColumnData,
  DeviceTableDeviceData,
} from '@ebiz/aditools-databook';
import CommentFilter from '@ebiz/ember-aditools-device-table/utils/comment-filter';
import EngineeringFilter from '@ebiz/ember-aditools-device-table/utils/engineering-filter';
import PriceFilter from '@ebiz/ember-aditools-device-table/utils/price-filter';
import EngineeringRangeValueFilter from '@ebiz/ember-aditools-device-table/utils/engineering-range-value-filter';
import MultivalueCheckboxFilter from '@ebiz/ember-aditools-device-table/utils/multivalue-checkbox-filter';
import type IntlService from 'ember-intl/services/intl';
import type { ImplementationType, FilterType as Type } from '@ebiz/aditools-filter/recommendations';

interface OpampsDialogArgs {
  close: () => void;
  title: string;
  updatePart: (part: string) => void;
  partName: string;
  limits: {
    allowedLimits: SallenKeyLowPassIdealOpAmp;
    recommendedLimits: SallenKeyLowPassIdealOpAmp;
    powerSpan: number;
    stageLetter: string;
  };
  vsPlus: number;
  vsMinus: number;
  selectionFilterOptions: {
    columns?: DeviceColumn<DeviceTableDeviceData>[] | undefined;
    filters?: PackedFilterValue[] | undefined;
    sortColumns?: SortColumnRef[] | undefined;
  };
  updateSelectionFilterOptions: (
    columns: DeviceColumn<DeviceTableDeviceData>[] | undefined,
    filters: PackedFilterValue[] | undefined,
    sortColumns: SortColumnRef[] | undefined,
  ) => void;
  implementation?: ImplementationType | undefined;
  filterType?: Type | undefined;
}

const options = {
  enableCellNavigation: true,
  enableColumnReorder: true,
  multiColumnSort: true,
  multiSelect: false,
  showHeaderRow: true,
  headerRowHeight: 30,
  frozenColumn: 0,
  explicitInitialization: true,
  parametersPanel: true,
};

type OpAmpDisplayItem = {
  selectable: boolean;
  disallowed: boolean;
  comments: string;
  partNumber: string;
  feedbackMethod: string;
  status: string;
  statusText: string;
  isLTSpiceOpAmp: string;
  vintage: string;
};

export default class OpampsDialog extends Component<OpampsDialogArgs> {
  @service declare intl: IntlService;
  @service('aditools-site') declare siteconfig: SiteService;
  @service opamps!: OpAmpsService;
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked selectedPartName = this.args.partName;
  @tracked pstColumns: DeviceColumn<DeviceTableDeviceData>[] = [];
  @tracked parameterSelectorGrid = false;
  @tracked _columns?: DeviceColumn<DeviceTableDeviceData>[];
  @tracked updatedfilters?: PackedFilterValue[];
  @tracked filters?: PackedFilterValue[];
  @tracked sortColumns?: SortColumnRef[];
  @tracked checkedColumns?: DeviceColumn<DeviceTableDeviceData>[];

  ////////////////////////////////////////////////////////////////////////////////
  // Properties                                                                 //
  ////////////////////////////////////////////////////////////////////////////////
  get data(): DeviceTableDeviceData[] {
    const opAmpData = this.opamps.getOpAmpsForDisplay(
      this.args.limits,
      this.args.vsPlus,
      this.args.vsMinus,
      this.args.implementation,
    );
    return this.buildOpAmpDisplay(opAmpData); //_indexOpAmps();
  }
  get columns(): DeviceColumn<DeviceTableDeviceData>[] {
    return (
      this._columns ??
      this.pstColumns.filter((x) => {
        return x.isDefault;
      })
    );
  }

  set columns(cols: DeviceColumn<DeviceTableDeviceData>[]) {
    this._columns = cols;
  }

  get options(): {
    enableCellNavigation: boolean;
    enableColumnReorder: boolean;
    multiColumnSort: boolean;
    multiSelect: boolean;
    showHeaderRow: boolean;
    headerRowHeight: number;
    frozenColumn: number;
    explicitInitialization: boolean;
  } {
    return options;
  }

  get noColumnsSelected(): boolean {
    return this.checkedColumns !== undefined && this.checkedColumns.length === 1;
  }

  get showDeviceTable() {
    return this.pstColumns.length > 0;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Support Functions                                                          //
  ////////////////////////////////////////////////////////////////////////////////
  setImportantColumns(pstColumns: DeviceColumn<DeviceTableDeviceData>[]): DeviceColumn<DeviceTableDeviceData>[] {
    let result: DeviceColumn<DeviceTableDeviceData>[] = [];
    const importantArray: DeviceColumn<DeviceTableDeviceData>[] = [];
    const importantColumns = [
      'price', // Price (1000+)
      'ltSpice', // LT Spice
      'statusText', // Status
      'temperature', // Temp Range
      'outVMinusHeadroomV', // Out V- Headroom
      'outVPlusHeadroomV', // Out V+ Headroom
      'inVMinusHeadroomV', // In V- Headroom
      'inVPlusHeadroomV', // In V+ Headroom
      'numberOfChannels', // # of Amps
      'vosMaxV', // Vos
      'ibiasMaxA', // Ibias
      'supplyCurrentTyp', // Iq/Amp
      'vNoiseDensity', // VNoise Density
      'vsSpanMaxV', // Vs span (max)
      'vsSpanMinV', // Vs span (min)
      'gbpHz', // GBP
      'comments', // Comments
      'partNumber', // Part Name
    ];

    importantColumns.forEach((item) => {
      const column = pstColumns.find((c) => c.id === item);
      if (column) {
        importantArray.push(column as DeviceColumn<DeviceTableDeviceData>);
      }
    });
    result = pstColumns.filter((item) => !importantArray.includes(item));
    importantArray.forEach((item) => {
      result.unshift(item);
    });

    return result;
  }

  getOpAmpSelectionColumns(): DeviceColumn<DeviceTableDeviceData>[] {
    const pstColumns = (this.opamps.databook! as DatabookStore)?.details['opAmp']?.columns.map(
      (column: DefaultColumnData) =>
        Object.assign(
          {},
          column,
          convertColumnDataForSelectionTable(column as DatabookColumnData, this.siteconfig.AdiLocale),
        ),
    );
    const siteUrl = this.siteconfig['SiteUrl'];
    const adiLocale = this.siteconfig.AdiLocale;

    const partFormatter = function (
      _row: number,
      _cell: number,
      value: string,
      _columnDef: unknown,
      dataContext: DeviceTableDeviceData,
    ): string {
      return [
        '<span class="',
        value.toLowerCase(),
        '-part-number-cell part-number-cell">',
        value,
        '<a target="_blank"',
        'class="recommended-part-item"',
        'rel="noopener noreferrer"',
        'data-action="part"',
        'data-partid="',
        value,
        '"',
        ' href="',
        siteUrl,
        '/',
        adiLocale,
        '/',
        dataContext['generic'],
        '">',
        '<span class="link-icon"></span>',
        '</a>',
        '</span>',
      ].join('');
    };

    pstColumns?.forEach((item) => {
      item.minWidth = 110;
      item.width = item.name ? (item.name as string).length * 12 : 110;

      if (item.id === 'partNumber') {
        item.resizable = false;
        item.width = 115;
        item.formatter = partFormatter;
      }

      // Set filter type for engineering and price filters
      if (item.filter && item.filter instanceof EngineeringFilter) {
        item.filter = new EngineeringRangeValueFilter('', '');
      } else if (item.filter && item.filter instanceof PriceFilter) {
        item.filter = new PriceFilter('LT');
      }

      if (
        item.id === 'inVPlusHeadroomV' || // In V+ Headroom (typ)
        item.id === 'inVMinusHeadroomV' || // In V- Headroom (typ)
        item.id === 'outVPlusHeadroomV' || // Out V+ Headroom (typ)
        item.id === 'outVMinusHeadroomV' || // Out V- Headroom (typ)
        item.id === 'temperature' || // Temp Range
        item.id === 'statusText' // Status
      ) {
        item.isDefault = true;
      }
    });

    const newColumns = [
      {
        id: 'comments',
        name: this.intl.t('dialogs.opamp.table.comments'),
        field: 'comments',
        classification: 'Other',
        width: 470,
        sortable: true,
        isDefault: true,
        unit: null,
        toolTip: this.intl.t('dialogs.opamp.table.comments'),
        filter: new CommentFilter([
          {
            value: '',
            display: this.intl.t('dialogs.opamp.table.commentsDisplay.showAll'),
          },
          {
            value: this.intl.t('dialogs.opamp.table.commentsDisplay.showRecommended'),
            match: this.intl.t('dialogs.opamp.table.commentsDisplay.showRecommendedMatch'),
            display: this.intl.t('dialogs.opamp.table.commentsDisplay.showRecommended'),
          },
          {
            value: this.intl.t('dialogs.opamp.table.commentsDisplay.showNotRecommended'),
            match: this.intl.t('dialogs.opamp.table.commentsDisplay.showNotRecommendedMatch'),
            display: this.intl.t('dialogs.opamp.table.commentsDisplay.showNotRecommended'),
          },
          {
            value: this.intl.t('dialogs.opamp.table.commentsDisplay.showNotAllowed'),
            match: this.intl.t('dialogs.opamp.table.commentsDisplay.showNotAllowedMatch'),
            display: this.intl.t('dialogs.opamp.table.commentsDisplay.showNotAllowed'),
          },
        ]),
      },
      {
        id: 'ltSpice',
        name: 'LTspice',
        field: 'ltSpice',
        classification: 'Other',
        width: 110,
        sortable: true,
        isDefault: true,
        unit: null,
        toolTip: 'LTSpice',
        filter: new MultivalueCheckboxFilter([
          {
            value: this.intl.t('dialogs.opamp.table.isLTSpiceDisplay.showYes'),
            display: this.intl.t('dialogs.opamp.table.isLTSpiceDisplay.showYes'),
            match: this.intl.t('dialogs.opamp.table.isLTSpiceDisplay.showYes'),
          },
          {
            value: this.intl.t('dialogs.opamp.table.isLTSpiceDisplay.showNo'),
            display: this.intl.t('dialogs.opamp.table.isLTSpiceDisplay.showNo'),
            match: this.intl.t('dialogs.opamp.table.isLTSpiceDisplay.showNo'),
          },
        ]),
      },
    ];
    newColumns.forEach((item) => {
      pstColumns?.push(item);
    });
    return this.setImportantColumns(pstColumns!);
  }

  buildOpAmpDisplay(opAmpData: OpAmpDisplayItem[]): DeviceTableDeviceData[] {
    const deviceData = convertDeviceDataForSelectionTable(
      (this.opamps.databook! as DatabookStore)!.details['opAmp']!.data!,
      this.pstColumns,
      true,
    );
    const data: DeviceTableDeviceData[] = [];
    for (let i = 0; i < opAmpData.length; i++) {
      for (let j = 0; j < deviceData.length; j++) {
        if (opAmpData[i] && deviceData[j]) {
          const opAmpDataItem = opAmpData[i] as OpAmpDisplayItem,
            deviceDataItem = deviceData[j] as DeviceTableDeviceData;
          if (opAmpDataItem.partNumber === deviceDataItem['partNumber']) {
            if (opAmpDataItem.feedbackMethod === 'Current') {
              deviceDataItem['display_gbpHz'] = 'CFB';
              deviceDataItem['value_gbpHz'] = 'CFB';
            } else {
              deviceDataItem['display_gbpHz'] =
                deviceDataItem['display_gbpHz'] !== '' ? deviceDataItem['display_gbpHz'] : '';
              deviceDataItem['value_gbpHz'] = deviceDataItem['value_gbpHz'] !== '' ? deviceDataItem['value_gbpHz'] : '';
            }
            deviceDataItem['comments'] = opAmpDataItem.comments;
            deviceDataItem['ltSpice'] =
              opAmpDataItem.isLTSpiceOpAmp === 'Yes'
                ? this.intl.t('dialogs.opamp.table.isLTSpiceDisplay.showYes')
                : this.intl.t('dialogs.opamp.table.isLTSpiceDisplay.showNo');
            deviceDataItem['disallowed'] = opAmpDataItem.disallowed;
            deviceDataItem['selectable'] = opAmpDataItem.selectable;
            deviceDataItem['vintage'] = opAmpDataItem.vintage;
            data.push(deviceDataItem);
          }
        }
      }
    }
    return data;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Component Actions                                                          //
  ////////////////////////////////////////////////////////////////////////////////
  @action insertingDeviceTable(): void {
    this.pstColumns = this.getOpAmpSelectionColumns();
    reduceColumnFilterValuesToThosePresent(this.data, this.pstColumns);
    if (this.args.selectionFilterOptions.columns) {
      this.columns = this.args.selectionFilterOptions.columns;
    }
    if (this.args.selectionFilterOptions.filters) {
      this.filters = this.args.selectionFilterOptions.filters;
    }
    if (this.args.selectionFilterOptions.sortColumns) {
      this.sortColumns = this.args.selectionFilterOptions.sortColumns;
    }
  }

  @action partChanged(partName: string): void {
    this.selectedPartName = partName;
  }

  @action onChooseParameters(): void {
    this.parameterSelectorGrid = true;
  }

  @action onAllParameters(): void {
    this.columns = this.pstColumns;
    if (this.updatedfilters) {
      this.filters = [...this.updatedfilters];
    }
    this.args.updateSelectionFilterOptions(this.columns, this.filters, this.sortColumns);
  }

  @action onResetParameters(): void {
    this.columns = this.pstColumns.filter((x) => {
      return x.isDefault;
    });
    this.updatedfilters = undefined;
    this.filters = undefined;
    this.sortColumns = undefined;
    this.checkedColumns = undefined;

    this.args.updateSelectionFilterOptions(undefined, undefined, undefined);
  }

  @action submitParameterSelector(closeDialog: () => void): void {
    if (this.checkedColumns) {
      this.columns = [...this.checkedColumns];
    }
    if (this.updatedfilters) {
      this.filters = [...this.updatedfilters];
    }
    this.args.updateSelectionFilterOptions(this.columns, this.filters, this.sortColumns);
    closeDialog();
  }

  @action closeParameterSelector(): void {
    this.parameterSelectorGrid = false;
    this.checkedColumns = undefined;
  }

  @action selectionChanged(newColumns: DeviceColumn<DeviceTableDeviceData>[]): void {
    this.checkedColumns = [...newColumns];
  }

  @action filterChanged(filters: PackedFilterValue[]): void {
    // 1. Get filters for any hidden columns
    const filtersOfHiddenCols: PackedFilterValue[] = [];
    if (this.updatedfilters) {
      this.updatedfilters.forEach((filterCol) => {
        const result = this.columns.find((col) => {
          return col.id === filterCol.columnId;
        });
        if (!result) {
          filtersOfHiddenCols.push(filterCol);
        }
      });
    }
    // 2. Merge filters for visible and hidden columns
    this.updatedfilters = filters.concat(filtersOfHiddenCols);
    this.args.updateSelectionFilterOptions(this.columns, this.updatedfilters, this.sortColumns);
  }

  @action sortChanged(sortColumns: SortColumnRef[]): void {
    this.sortColumns = sortColumns;
    this.args.updateSelectionFilterOptions(
      this.columns,
      this.filters === undefined ? this.updatedfilters : this.filters,
      this.sortColumns,
    );
  }

  @action applyAndClose(closeDialog: () => void) {
    if (this.selectedPartName) {
      this.args.updatePart(this.selectedPartName);
    }
    closeDialog();
  }
}
